import { Box, useMediaQuery } from "@mui/material";
import { Chart as ChartJS, ArcElement, Title, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { useRef, useEffect } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import annotationPlugin from "chartjs-plugin-annotation";
import { useTheme } from "@emotion/react";
import { formatNumberWithLocale } from "../../utils/charts";

ChartJS.register(ArcElement, Title, Tooltip, Legend, ChartDataLabels, annotationPlugin);

export default function DonutChart({ data, showDataLabels, hideSubregions, title, cutout = "50%" }) {
    const theme = useTheme();
    const ref = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        ChartJS.defaults.font.size = isMobile ? 9 : 11;
    }, [isMobile]);

    const labels = (data.labels || []).map((l) => (Array.isArray(l) ? l[0] : l));
    const dataset = data.datasets?.[0] || { data: [], backgroundColor: [] };
    const total = dataset.data.reduce((sum, v) => sum + (Number(v) || 0), 0);
    const { totalValue } = data.info || {};

    const chartData = {
        labels,
        datasets: [
            {
                ...dataset,
                hoverOffset: 6,
                borderWidth: 0,
            },
        ],
    };

    const unit = data.info?.unit || "";
    const centerValue = totalValue ?? total;

    const options = {
        cutout,
        layout: { padding: { top: 40, right: isMobile ? 8 : 12, bottom: 20, left: isMobile ? 5 : 10 } },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: false, // will be toggled to true for PNG export
                text: title,
                font: { size: 16, lineHeight: 1.2, weight: 'bold' },
                padding: { top: 0, left: 0, right: 0, bottom: 30 },
                color: theme.palette.primary.fontColor,
                position: 'top',
            },
            annotation: {
                annotations: {
                    dLabel: {
                        type: 'doughnutLabel', // total value and unit
                        content: ({ chart }) => [
                            `${data.info?.latestYear ? `(${data.info.latestYear})` : ""}`,
                            "",
                            totalValue ? `${formatNumberWithLocale(totalValue)}\n` : "",
                            unit
                        ],
                        font: [{ size: 20, color: theme.palette.primary.fontColor }, { size: 18, color: theme.palette.primary.fontColor }, { size: 18, color: theme.palette.primary.fontColor }],
                        color: ['black']
                    }
                }
            },

            legend: {
                position: "right",
                align: "middle",
                labels: {
                    usePointStyle: true,
                    padding: isMobile ? 8 : 12,
                    color: theme.palette.primary.fontColor,
                },
            },
            datalabels: {
                color: theme.palette.primary.fontColor,
                align: "end",
                anchor: "end",
                clamp: true,
                display: showDataLabels ? "auto" : false,
                font: { size: 10 },
                padding: 4,
                formatter: (value) => {
                    const v = Number(value) || 0;
                    if (!total || !v) return "";
                    return `${((v / total) * 100).toFixed(1)}%`;
                },
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label || "";
                        const value = Number(context.parsed) || 0;
                        const unit = data.info?.unit || "";
                        const pct = total ? ((value / total) * 100).toFixed(1) + "%" : "";
                        const yr = data.info?.latestYear ? `, ${data.info.latestYear}` : "";
                        return `${label}: ${formatNumberWithLocale(value)} ${unit} (${pct}${yr})`;
                    },
                    title: () => String(data.info?.latestYear ?? "")
                },
                backgroundColor: "#f1f1f1",
                titleColor: "#000",
                bodyColor: "#000",
                borderColor: "#ccc",
                borderWidth: 1,
            },
            centerText: {
                value: formatNumberWithLocale(Number(centerValue)),
                unit,
                color: theme.palette.primary.fontColor,
            }
        },
    };

    return (
        <Box className="max-chart-height" sx={{ position: "relative" }}>
            <Doughnut ref={ref} options={options} data={chartData} />
        </Box>
    );
}